import type { Context, Handle } from '../../contract/index.js';
import { LoaderError } from '../containment.js';
import { buildSrc, hostServedTemplateRefusal } from '../iframe/buildSrc.js';
import {
  originRefusal,
  templateOrigin,
  type HostLocation,
} from '../iframe/origin.js';
import type {
  Integration,
  IntegrationMountArgs,
  LoadedArtifact,
  RegistryEntry,
} from '../integrations.js';
import type { Telemetry } from '../telemetry.js';
import {
  elementAttributes,
  elementError,
  type ScriptElement,
} from './customElement.js';

/** What a `script` entry may set as `referrerPolicy`. */
export const SCRIPT_REFERRER_POLICIES = [
  'strict-origin',
  'strict-origin-when-cross-origin',
  'origin',
  'no-referrer',
] as const;

export type ScriptReferrerPolicy = (typeof SCRIPT_REFERRER_POLICIES)[number];

/** The vendor learns which site embeds it, never the page path or query. */
export const DEFAULT_SCRIPT_REFERRER_POLICY: ScriptReferrerPolicy =
  'strict-origin';

export const SCRIPT_ELEMENT_ID_PREFIX = 'mfe-script-';

/**
 * Reported when the script fails to load. Without `element` the placement is
 * still ready, so the zero-height slot does not hold a fallback. With
 * `element` the element can never be defined, so the placement falls back at
 * once and each one reports the failure.
 */
export const SCRIPT_LOAD_FAILED_REASON = 'script-load-failed';

/** Reported when a placement fills a different `src` than the script already on the page. */
export const SCRIPT_SRC_MISMATCH_REASON = 'script-src-mismatch';

/** Named in the refusal when the element id is held by an element the loader did not create. */
export const SCRIPT_ID_CONFLICT_REASON = 'script-id-conflict';

/** The fields a `script` registry entry adds. */
export interface ScriptEntry extends RegistryEntry {
  readonly integration: 'script';
  readonly origins: readonly string[];
  /** A URL template. Each `{name}` in its path or query is a declared input. */
  readonly src: string;
  readonly consentCategory: string;
  readonly vettingApproval: string;
  /** Defaults to `true`. */
  readonly async?: boolean;
  /** Defaults to `strict-origin`. */
  readonly referrerPolicy?: ScriptReferrerPolicy;
  /** Lets `src` be on the host page's own origin. */
  readonly hostServed?: boolean;
  /** A custom element the script defines, mounted in the slot. */
  readonly element?: ScriptElement;
}

export interface ScriptIntegrationOptions {
  readonly telemetry: Telemetry;
  /** The host page's location. Without one, every script is refused. */
  readonly location: HostLocation | undefined;
  /** Accepts `http:` scripts. For the local harness only. */
  readonly allowInsecureScripts?: boolean;
}

interface ScriptPlan extends LoadedArtifact {
  readonly template: string;
  readonly origin: string;
  readonly async: boolean;
  readonly referrerPolicy: ScriptReferrerPolicy;
  readonly hostServed: boolean;
  readonly elementId: string;
  readonly element: ScriptElement | undefined;
}

type ScriptState = 'pending' | 'loaded' | 'error';
type SettledState = Exclude<ScriptState, 'pending'>;

interface ScriptRecord {
  /** The filled src, kept here rather than in an attribute other page scripts read. */
  readonly src: string;
  state: ScriptState;
  /** Placements waiting for the script. Each removes itself when it ends. */
  readonly waiting: Set<(state: SettledState) => void>;
  /** Set once a placement has reported the failed load, so it is counted once per page. */
  failureReported: boolean;
}

/**
 * Every script the loader injected, by document and module id, across loader
 * instances. The record outlives its element, so a vendor that removes its
 * own tag is not injected again, and a placement that ends before the script
 * settles never leaves a later one waiting for an event that already fired.
 */
const injected = new WeakMap<Document, Map<string, ScriptRecord>>();

/** Drops the records for `doc`. For tests that reuse one document. */
export function forgetInjectedScripts(doc: Document): void {
  injected.delete(doc);
}

/** The placements still waiting on a script that has not settled. For tests. */
export function waitingPlacements(doc: Document): number {
  let count = 0;
  injected.get(doc)?.forEach((record) => {
    count += record.waiting.size;
  });
  return count;
}

function injectedIn(doc: Document): Map<string, ScriptRecord> {
  let records = injected.get(doc);
  if (!records) {
    records = new Map();
    injected.set(doc, records);
  }
  return records;
}

function stringList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];
}

function isReferrerPolicy(value: unknown): value is ScriptReferrerPolicy {
  return (SCRIPT_REFERRER_POLICIES as readonly unknown[]).includes(value);
}

function nonEmpty(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * The built-in integration for a vetted vendor's classic script. It fetches
 * nothing: `load` checks the entry and `mount` puts one `<script>` in the
 * document head per entry per page, whatever the number of placements. A
 * placement is ready when that script loads or fails. The script, its UI and
 * its globals stay until the page reloads.
 */
export class ScriptIntegration implements Integration {
  constructor(private readonly options: ScriptIntegrationOptions) {}

  /** Covers every field `load` checks, so an edited entry is never served an earlier check. */
  readonly cacheKey = (entry: RegistryEntry): string => {
    const {
      src,
      origins,
      async,
      referrerPolicy,
      consentCategory,
      vettingApproval,
      hostServed,
      element,
    } = entry as Partial<ScriptEntry>;
    return `script:${entry.moduleId}:${JSON.stringify([
      src,
      origins,
      async,
      referrerPolicy,
      consentCategory,
      vettingApproval,
      hostServed,
      element,
      element ? Object.keys(entry.inputs ?? {}) : undefined,
    ])}`;
  };

  async load(entry: RegistryEntry): Promise<LoadedArtifact> {
    return this.plan(entry);
  }

  mount({
    entry,
    loaded,
    container,
    context,
    host,
    slot,
    signal,
    fail,
  }: IntegrationMountArgs): Handle {
    const plan = loaded as ScriptPlan;
    const src = buildSrc(plan.template, context.inputs, plan.origin, 'script', {
      hostServed: plan.hostServed,
    });
    const record = this.attach(
      container.ownerDocument,
      plan,
      entry.moduleId,
      src,
      slot,
    );
    let release = (): void => undefined;
    const handle = plan.element
      ? new ElementHandle(
          () => release(),
          container,
          plan.element,
          context.inputs,
        )
      : new ScriptHandle(() => release());
    const onSettled = (state: SettledState): void => {
      release();
      if (handle.ended || signal.aborted) {
        return;
      }
      if (state === 'error' && handle instanceof ElementHandle) {
        fail(
          new LoaderError('load-failed', 'The script failed to load', {
            faultReason: SCRIPT_LOAD_FAILED_REASON,
          }),
        );
        return;
      }
      if (state === 'error' && !record.failureReported) {
        record.failureReported = true;
        this.options.telemetry.error(
          new LoaderError('load-failed', 'The script failed to load'),
          { slot, moduleId: entry.moduleId, reason: SCRIPT_LOAD_FAILED_REASON },
        );
      }
      if (!(handle instanceof ElementHandle)) {
        host.ready();
        return;
      }
      if (state === 'loaded') {
        handle.whenDefined().then(
          () => {
            if (!handle.ended && !signal.aborted) {
              host.ready();
            }
          },
          () => undefined,
        );
      }
    };
    if (record.state === 'pending') {
      release = () => {
        record.waiting.delete(onSettled);
        signal.removeEventListener('abort', release);
      };
      record.waiting.add(onSettled);
      signal.addEventListener('abort', release, { once: true });
    } else {
      const settled = record.state;
      queueMicrotask(() => onSettled(settled));
    }
    return handle;
  }

  /** The record of the entry's element, injecting it when the page has none. */
  private attach(
    doc: Document,
    plan: ScriptPlan,
    moduleId: string,
    src: string,
    slot: string,
  ): ScriptRecord {
    const records = injectedIn(doc);
    const record = records.get(moduleId);
    if (!record) {
      if (doc.getElementById(plan.elementId)) {
        throw new LoaderError(
          'load-failed',
          `The element id ${plan.elementId} is taken by an element the loader did not create`,
          { faultReason: SCRIPT_ID_CONFLICT_REASON },
        );
      }
      const created = inject(doc, plan, moduleId, src);
      records.set(moduleId, created);
      return created;
    }
    if (record.src !== src) {
      this.options.telemetry.error(
        new LoaderError(
          'load-failed',
          'The script is already on the page with a different src',
        ),
        { slot, moduleId, reason: SCRIPT_SRC_MISMATCH_REASON },
      );
    }
    return record;
  }

  private plan(entry: RegistryEntry): ScriptPlan {
    const host = this.options.location;
    if (!host) {
      throw new LoaderError(
        'load-failed',
        'The page has no location to check the script src against',
      );
    }
    const {
      src,
      origins,
      async,
      referrerPolicy,
      consentCategory,
      vettingApproval,
      hostServed,
      element,
    } = entry as Partial<ScriptEntry>;
    if (!nonEmpty(vettingApproval)) {
      throw new LoaderError(
        'load-failed',
        'The script entry has no vetting approval',
      );
    }
    if (!nonEmpty(consentCategory)) {
      throw new LoaderError(
        'load-failed',
        'The script entry has no consent category',
      );
    }
    if (typeof src !== 'string') {
      throw new LoaderError('load-failed', 'The script entry has no src');
    }
    const allowInsecure = this.options.allowInsecureScripts === true;
    const origin = templateOrigin(src, allowInsecure);
    if (origin === undefined) {
      throw new LoaderError(
        'load-failed',
        'The script src is not an absolute URL with a literal origin',
      );
    }
    if (hostServed !== undefined && typeof hostServed !== 'boolean') {
      throw new LoaderError(
        'load-failed',
        'The script hostServed is not a boolean',
      );
    }
    const refusal = originRefusal(origin, stringList(origins), host, {
      allowInsecure,
      hostServed,
    });
    if (refusal !== undefined) {
      throw new LoaderError(
        'load-failed',
        `The script src origin ${origin} ${refusal}`,
      );
    }
    const pathRefusal =
      hostServed === true ? hostServedTemplateRefusal(src) : undefined;
    if (pathRefusal !== undefined) {
      throw new LoaderError(
        'load-failed',
        `The host-served script src ${pathRefusal}`,
      );
    }
    if (async !== undefined && typeof async !== 'boolean') {
      throw new LoaderError('load-failed', 'The script async is not a boolean');
    }
    if (referrerPolicy !== undefined && !isReferrerPolicy(referrerPolicy)) {
      throw new LoaderError(
        'load-failed',
        'The script referrerPolicy is not one of the allowed policies',
      );
    }
    const elementRefusal =
      element === undefined
        ? null
        : elementError(element, Object.keys(entry.inputs ?? {}));
    if (elementRefusal !== null) {
      throw new LoaderError('load-failed', `The script ${elementRefusal}`);
    }
    return {
      template: src,
      origin,
      async: async ?? true,
      referrerPolicy: referrerPolicy ?? DEFAULT_SCRIPT_REFERRER_POLICY,
      hostServed: hostServed === true,
      elementId: `${SCRIPT_ELEMENT_ID_PREFIX}${entry.moduleId}`,
      element,
    };
  }
}

/**
 * No `crossorigin`: most vendors send no CORS headers. The cost is that the
 * vendor's runtime errors reach the page as "Script error." with no source.
 */
function inject(
  doc: Document,
  plan: ScriptPlan,
  moduleId: string,
  src: string,
): ScriptRecord {
  const script = doc.createElement('script');
  script.id = plan.elementId;
  script.async = plan.async;
  script.setAttribute('referrerpolicy', plan.referrerPolicy);
  script.setAttribute('data-mfe-module', moduleId);
  const record: ScriptRecord = {
    src,
    state: 'pending',
    waiting: new Set(),
    failureReported: false,
  };
  const onSettled = (state: SettledState) => () => {
    if (record.state !== 'pending') {
      return;
    }
    record.state = state;
    const waiting = Array.from(record.waiting);
    record.waiting.clear();
    for (const notify of waiting) {
      notify(state);
    }
  };
  script.addEventListener('load', onSettled('loaded'), { once: true });
  script.addEventListener('error', onSettled('error'), { once: true });
  script.src = src;
  doc.head.append(script);
  return record;
}

/** One placement. The script cannot take new inputs, and it outlives the placement. */
class ScriptHandle implements Handle {
  ended = false;

  constructor(private readonly release: () => void) {}

  update(): void {
    return;
  }

  unmount(): void {
    this.ended = true;
    this.release();
  }
}

/**
 * One placement of an entry with `element`: the vendor's custom element in the
 * slot container. Attribute values are set with `setAttribute` only, so an
 * input is never read as markup. An update sets them again on the same
 * element, and unmount removes it. The script still stays on the page.
 */
class ElementHandle extends ScriptHandle {
  private readonly node: HTMLElement;

  constructor(
    release: () => void,
    container: HTMLElement,
    private readonly element: ScriptElement,
    inputs: Context['inputs'],
  ) {
    super(release);
    this.node = container.ownerDocument.createElement(element.tag);
    this.apply(inputs);
    container.append(this.node);
  }

  /** Never settles when the page has no custom element registry, so the deadline applies. */
  whenDefined(): Promise<unknown> {
    const registry = this.node.ownerDocument.defaultView?.customElements;
    return registry
      ? registry.whenDefined(this.element.tag)
      : new Promise(() => undefined);
  }

  override update(context?: Context): void {
    if (!this.ended && context) {
      this.apply(context.inputs);
    }
  }

  override unmount(): void {
    super.unmount();
    this.node.remove();
  }

  private apply(inputs: Context['inputs']): void {
    for (const [name, value] of elementAttributes(this.element, inputs)) {
      if (value === undefined) {
        this.node.removeAttribute(name);
      } else {
        this.node.setAttribute(name, value);
      }
    }
  }
}
